import { useRef, useState } from "react";

import { usePostHog } from "posthog-js/react";

import { useDisclosure } from "hooks";

import { ClientAppEvent } from "../../../../constants";
import { extractDateFromIsoString } from "../../../../utils";

import type { SubmitQuotePreviewShipment } from "./types";
import type { SubmitQuoteFormState } from "../../../Carrier/components/SubmitQuoteForm/types";

interface UseUnauthenticatedQuoteFormStateProps<TFormState> {
  shipment: SubmitQuotePreviewShipment;
  initialState: TFormState;
}

interface UseUnauthenticatedQuoteFormStateResult<TFormState> {
  formState: TFormState;
  onValueChanged: <T>(key: keyof TFormState, value: T) => void;
  onPopulateShipmentRequirements: VoidFunction;
  snackbarOpened: boolean;
  onSnackbarClose: VoidFunction;
  carrierTeammatesOpened: boolean;
  closeCarrierTeammates: VoidFunction;
  openCarrierTeammates: VoidFunction;
}

/**
 * The form-state half the single-shipment and multi-shipment-project landings
 * hold in common: draft state, the first-interaction funnel event, and the
 * populate-from-shipment shortcut with its confirmation snackbar. The project
 * form state is a superset of the single one, so both drive the same hook
 * (docs/engineering/design/carrier_submit_quote_command_link_design.md D2).
 */
export function useUnauthenticatedQuoteFormState<
  TFormState extends SubmitQuoteFormState,
>(
  props: UseUnauthenticatedQuoteFormStateProps<TFormState>,
): UseUnauthenticatedQuoteFormStateResult<TFormState> {
  const { shipment, initialState } = props;
  const posthog = usePostHog();
  const [formState, setFormState] = useState<TFormState>(initialState);
  const formStartedRef = useRef(false);
  const {
    isOpen: snackbarOpened,
    onClose: onSnackbarClose,
    onOpen: openSnackbar,
  } = useDisclosure(false);
  const {
    isOpen: carrierTeammatesOpened,
    onClose: closeCarrierTeammates,
    onOpen: openCarrierTeammates,
  } = useDisclosure(false);

  /**
   * A ref rather than state: populating the requirements changes three fields in
   * one click, and state would still read `false` on all three, reporting the
   * carrier as having started the form three times.
   */
  const markFormStarted = (): void => {
    if (formStartedRef.current) {
      return;
    }
    formStartedRef.current = true;
    posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_FORM_STARTED, {
      shipment_id: shipment.info.id,
      shipment_type: shipment.info.type,
    });
  };

  const onValueChanged = <T>(key: keyof TFormState, value: T): void => {
    markFormStarted();
    setFormState((previousState) => ({ ...previousState, [key]: value }));
  };

  const onPopulateShipmentRequirements = (): void => {
    markFormStarted();
    setFormState((previousState) => ({
      ...previousState,
      pickupStart: extractDateFromIsoString(shipment.first_point.start_time),
      deliveryEta: extractDateFromIsoString(shipment.last_point.start_time),
      ...(shipment.equipments.length > 0
        ? { equipmentType: shipment.equipments[0].type }
        : {}),
    }));
    posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_REQUIREMENTS_APPLIED, {
      shipment_id: shipment.info.id,
    });
    openSnackbar();
  };

  return {
    formState,
    onValueChanged,
    onPopulateShipmentRequirements,
    snackbarOpened,
    onSnackbarClose,
    carrierTeammatesOpened,
    closeCarrierTeammates,
    openCarrierTeammates,
  };
}
